import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Search,
  Filter as FilterIcon,
  X,
  Users,
  FileCheck2,
  AlertTriangle,
  Clock,
  BookOpen,
  Eye,
  PackageCheck,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Prüfmappe — UX 2.0 (Juni 2026)
 *
 * Beantwortet ausschließlich: „Ist mein Betrieb prüfbereit?"
 * Jede Kennzahl ist klickbar und filtert den Listenbereich darunter dynamisch
 * (keine Navigation, keine Modals, kein Reload). Bestehende API-Responses werden
 * unverändert weiterverwendet – KEINE neuen Endpunkte/Datenfelder.
 *
 * Status-Heuristik (DGUV V1 §4 — Unterweisung mindestens jährlich):
 *   - aktuell:    completed_at innerhalb der letzten 12 Monate
 *   - bald_faellig: 12–13 Monate
 *   - abgelaufen: > 13 Monate
 */

function formatDate(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

const ALL = "__all__";

const DOC_TYPE_LABEL = {
  unterweisung: "Unterweisung",
  weltfreigabe: "Weltfreigabe",
  wirksamkeitskontrolle: "Wirksamkeitskontrolle",
  handschutzplan: "Handschutzplan",
  substitutionserklaerung: "Substitutionserklärung",
  sicherheitsdatenblatt: "Sicherheitsdatenblatt",
};

const STATUS_LABEL = {
  aktuell: "Aktuell",
  bald_faellig: "Bald fällig",
  abgelaufen: "Abgelaufen",
};

const STATUS_BADGE = {
  aktuell: "bg-emerald-50 text-emerald-800 border-emerald-200",
  bald_faellig: "bg-amber-50 text-amber-800 border-amber-200",
  abgelaufen: "bg-rose-50 text-rose-800 border-rose-200",
};

// Wirksamkeitskontrollen (Schritt 6, NUR Darstellung): ergebnisbasiertes Badge –
// NICHT die Nachweis-Statuslogik/computeStatus verwenden.
const WK_RESULT_LABEL = {
  wirksam: "Wirksam",
  teilweise_wirksam: "Teilweise wirksam",
  nicht_wirksam: "Nicht wirksam",
};
const WK_RESULT_BADGE = {
  wirksam: "bg-emerald-50 text-emerald-800 border-emerald-200",
  teilweise_wirksam: "bg-amber-50 text-amber-800 border-amber-200",
  nicht_wirksam: "bg-rose-50 text-rose-800 border-rose-200",
};

// Status einzelner Aufgaben (offene/überfällige Listen).
const ASSIGNMENT_BADGE = {
  offen: { label: "Offen", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  erledigt: { label: "Erledigt", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  ueberfaellig: { label: "Überfällig", cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

const VIEW_LABEL = {
  mitarbeiter: "Mitarbeiter",
  nachweise: "Alle Nachweise",
  offen: "Offene Aufgaben",
  ueberfaellig: "Überfällige Aufgaben",
  welten: "Aktive Dokumentationswelten",
};

const PERIOD_OPTIONS = [
  { value: ALL, label: "Alle Zeiträume" },
  { value: "7d", label: "Letzte 7 Tage" },
  { value: "30d", label: "Letzte 30 Tage" },
  { value: "90d", label: "Letzte 90 Tage" },
  { value: "12m", label: "Letzte 12 Monate" },
  { value: "ytd", label: "Aktuelles Jahr" },
];

function computeStatus(n) {
  if (n.nachweis_type === "weltfreigabe") return "aktuell";
  if (!n.completed_at) return "aktuell";
  const completed = new Date(n.completed_at);
  if (Number.isNaN(completed.getTime())) return "aktuell";

  const now = new Date();
  const dueAt = new Date(completed);
  dueAt.setFullYear(dueAt.getFullYear() + 1);
  const grace = new Date(dueAt);
  grace.setMonth(grace.getMonth() + 1);

  if (now > grace) return "abgelaufen";
  if (now > dueAt) return "bald_faellig";
  return "aktuell";
}

function inPeriod(iso, period) {
  if (period === ALL) return true;
  if (!iso) return false;
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return false;
  const now = new Date();
  if (period === "7d") {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 7);
    return start >= cutoff;
  }
  if (period === "30d") {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 30);
    return start >= cutoff;
  }
  if (period === "90d") {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 90);
    return start >= cutoff;
  }
  if (period === "12m") {
    const cutoff = new Date(now);
    cutoff.setFullYear(cutoff.getFullYear() - 1);
    return start >= cutoff;
  }
  if (period === "ytd") {
    return start.getFullYear() === now.getFullYear();
  }
  return true;
}

// ---------------------------------------------------------------------------
// KPI-Karte (klickbar = Filter)
// ---------------------------------------------------------------------------

function KpiCard({ label, value, icon: Icon, iconSrc, tone = "default", testId, active, onClick }) {
  const toneClass = {
    default: "text-slate-900",
    warning: "text-amber-700",
    danger: "text-rose-700",
    success: "text-emerald-700",
  }[tone];
  const iconBg = {
    default: "bg-slate-900 text-white",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-rose-100 text-rose-700",
    success: "bg-emerald-100 text-emerald-700",
  }[tone];
  return (
    <Card
      data-testid={testId}
      role="button"
      aria-pressed={active}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "bg-white p-4 transition-all cursor-pointer border hover:shadow-md hover:border-slate-300",
        active
          ? "border-slate-900 ring-2 ring-slate-900 shadow-md"
          : "border-slate-200 shadow-sm"
      )}
    >
      <div className="flex items-start gap-2">
        {/* UI-REDESIGN Ergänzung 3. Kapitel (Aug 2026, betreibergenehmigt):
            freigegebene Foto-Icons ersetzen lucide-Symbole für vier von fünf
            KPI-Kacheln. Kachel "Mitarbeiter" behält bewusst das lucide-Icon.
            Bei gesetztem `iconSrc` wird das PNG ohne farbige Hinterlegung
            angezeigt (Foto trägt seine Farbstimmung selbst).
            Icon-Größe bewusst 40 px (nicht 48 px), damit lange Labels wie
            "Dokumentationen aktiv" auf allen Bildschirmbreiten in die
            Kachel passen (5-Spalten-Grid auf lg). */}
        {iconSrc ? (
          <img
            src={iconSrc}
            alt=""
            className="w-10 h-10 object-contain shrink-0 select-none"
            draggable="false"
            data-testid={`${testId}-icon-img`}
          />
        ) : (
          <div
            className={cn(
              "w-9 h-9 rounded-md flex items-center justify-center shrink-0",
              iconBg
            )}
          >
            <Icon className="w-4 h-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium break-words">
            {label}
          </div>
          <div
            className={cn(
              "font-heading text-2xl font-bold mt-0.5 leading-tight",
              toneClass
            )}
            data-testid={`${testId}-value`}
          >
            {value}
          </div>
        </div>
      </div>
    </Card>
  );
}

function EmptyList({ icon: Icon, title, text }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm p-10 text-center">
      <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">{text}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Hauptkomponente
// ---------------------------------------------------------------------------

export default function Pruefmappe() {
  const navigate = useNavigate();
  const [nachweise, setNachweise] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState(null);
  // Schritt 6 (NUR Darstellung): erledigte Wirksamkeitskontrollen in EIGENEM State –
  // NIEMALS in `nachweise` (schützt KPI „Nachweise gesamt"/stats.nachweise).
  const [wkErledigt, setWkErledigt] = useState([]);
  // HSP-Integration Schritt 1 (28.08.2026): Handschutzplan-WORM-Zeugen in
  // EIGENEM State (analog wkErledigt) — niemals in `nachweise` (KPI-Schutz).
  const [hspPlaene, setHspPlaene] = useState([]);
  // HSD-Integration Schritt 2 (28.08.2026): Substitutionserklärungs-Zeugen
  // in EIGENEM State — analog hspPlaene/wkErledigt (KPI-Schutz).
  const [hsdZeugen, setHsdZeugen] = useState([]);
  // SDB-Integration Schritt 3 (29.08.2026): archivierte Original-SDBs
  // in EIGENEM State — analog hsdZeugen (KPI-Schutz).
  const [sdbZeugen, setSdbZeugen] = useState([]);

  // Aktive Kennzahl = aktiver Filter. null bis Standardansicht gesetzt ist.
  const [view, setView] = useState(null);

  const [filterEmployee, setFilterEmployee] = useState(ALL);
  const [filterPeriod, setFilterPeriod] = useState(ALL);
  const [filterDocType, setFilterDocType] = useState(ALL);
  const [filterStatus, setFilterStatus] = useState(ALL);
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadingPackage, setDownloadingPackage] = useState(false);

  const handleOpenWkPdf = async (id) => {
    try {
      await api.openWirksamkeitPdf(id);
    } catch (e) {
      toast.error("PDF konnte nicht geöffnet werden.");
    }
  };

  // HSP-Integration: lesbarer PDF-Abzug des WORM-Zeugen im neuen Tab
  // (UX-Korrektur 29.08.2026 — vorher rohes JSON, für Prüfer unlesbar).
  const handleOpenHspZeuge = async (documentNumber) => {
    try {
      const { blob } = await api.fetchHandschutzplanBelegPdfBlob(documentNumber);
      const url = URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Der Nachweis-Beleg konnte nicht geöffnet werden.");
    }
  };

  // HSD-Integration: lesbarer PDF-Abzug des Substitutions-Zeugen im neuen
  // Tab (UX-Korrektur 29.08.2026 — vorher rohes JSON).
  const handleOpenHsdZeuge = async (documentNumber) => {
    try {
      const { blob } = await api.fetchSubstitutionBelegPdfBlob(documentNumber);
      const url = URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Der Nachweis-Beleg konnte nicht geöffnet werden.");
    }
  };

  // SDB-Integration: „Öffnen" liefert direkt das Original-SDB-PDF des
  // Herstellers (bereits ein lesbares PDF — kein JSON-Umweg, Schritt 3).
  const handleOpenSdbOriginal = async (documentNumber) => {
    try {
      const { blob } = await api.fetchSdbOriginalPdfBlob(documentNumber);
      const url = URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Der Nachweis-Beleg konnte nicht geöffnet werden.");
    }
  };

  const handleDownloadPackage = async () => {
    if (downloadingPackage) return;
    setDownloadingPackage(true);
    const t = toast.loading(
      "BG-Prüfpaket wird erstellt – WORM-Originale werden aus dem Archiv geladen …"
    );
    try {
      const filename = await api.downloadBgPruefpaket();
      toast.success(`BG-Prüfpaket erstellt: ${filename}`, { id: t });
    } catch (e) {
      toast.error("BG-Prüfpaket konnte nicht erstellt werden.", { id: t });
    } finally {
      setDownloadingPackage(false);
    }
  };

  useEffect(() => {
    api.listNachweise().then(setNachweise);
    api.listEmployeesAll().then(setEmployees);
    api.listAssignments().then(setAssignments);
    api.listCourses().then(setCourses);
    api.getCompany().then(setCompany);
    api.getStats().then((s) => {
      setStats(s);
      setView((prev) => prev ?? (s.ueberfaellig > 0 ? "ueberfaellig" : "nachweise"));
    });
    // Schritt 6: erledigte WK fehlertolerant nachladen (darf die Seite nie blockieren).
    api.listErledigteWirksamkeit().then(setWkErledigt).catch(() => {});
    // HSP-Integration: Handschutzpläne fehlertolerant nachladen. Die Route
    // hängt am hazmat-Modul-Gate — 403 (Modul inaktiv) wird still geschluckt
    // (Muster: Waisen-Zähler in Handschutzplan.jsx), kein Toast, keine Zeilen.
    api
      .listHandschutzplaene()
      .then((rows) => setHspPlaene(Array.isArray(rows) ? rows : []))
      .catch(() => {});
    // HSD-Integration: Substitutionszeugen fehlertolerant nachladen —
    // Modul-Gate-403 wird still geschluckt (identisches Muster wie HSP).
    api
      .listSubstitutionZeugen()
      .then((rows) => setHsdZeugen(Array.isArray(rows) ? rows : []))
      .catch(() => {});
    // SDB-Integration: Original-SDB-Zeugen fehlertolerant nachladen —
    // Modul-Gate-403 wird still geschluckt (identisches Muster).
    api
      .listSdbZeugen()
      .then((rows) => setSdbZeugen(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, []);

  const activeView = view || "nachweise";

  // Klick auf aktive Kennzahl hebt den Filter auf → zurück zur Standardansicht.
  const handleKpiClick = (key) => setView((prev) => (prev === key ? "nachweise" : key));

  // --- Nachweise (mit abgeleitetem Status) ---------------------------------
  const enriched = useMemo(
    () =>
      nachweise.map((n) => ({
        ...n,
        status: computeStatus(n),
        doc_type_label: DOC_TYPE_LABEL[n.nachweis_type] || n.nachweis_type || "–",
      })),
    [nachweise]
  );

  // Schritt 6: erledigte WK in die Zeilenform der bestehenden Tabelle mappen
  // (eigener Dokumenttyp). Bleibt strikt getrennt vom `nachweise`-Array.
  const wkRows = useMemo(
    () =>
      (wkErledigt || []).map((w) => ({
        id: w.id,
        is_wk: true,
        nachweis_type: "wirksamkeitskontrolle",
        doc_type_label: "Wirksamkeitskontrolle",
        employee_name: w.checked_by_name || "—",
        checked_by_name: w.checked_by_name || "—",
        course_title: w.welt_title || "Welt",
        course_version: w.welt_version,
        completed_at: w.checked_at,
        document_number: w.document_number,
        result: w.result,
        worm_status: w.worm_status,
        pdf_available: w.worm_status === "archived" && !!w.pdf_storage_key,
        status: undefined,
      })),
    [wkErledigt]
  );

  // Gemeinsame Anzeige-Liste (Unterweisungsnachweise + WK). KPI bleibt unberührt.
  // HSP-Integration: nur Pläne mit tatsächlich archiviertem WORM-Zeugen
  // (last_worm_* gesetzt) — Pläne mit Content, aber ohne Beleg, erscheinen nicht.
  const hspRows = useMemo(
    () =>
      (hspPlaene || [])
        .filter((p) => p.last_worm_document_number && p.last_worm_storage_key)
        .map((p) => ({
          id: `hsp-${p.id || p.einsatzbereich_id}`,
          is_hsp: true,
          nachweis_type: "handschutzplan",
          doc_type_label: "Handschutzplan",
          // Handschutzplan hat keine employee_id — der Einsatzbereich-Name
          // übernimmt die Personen-Spalte (analog WK: checked_by_name).
          employee_name:
            (p.content || {}).einsatzbereich_name || p.einsatzbereich_id || "—",
          course_title:
            (p.content || {}).titel || "Hautschutz- und Handschutzplan",
          completed_at: p.last_worm_archived_at || p.generated_at,
          document_number: p.last_worm_document_number,
          status: undefined,
        })),
    [hspPlaene]
  );

  // HSD-Integration: Zeugen kommen bereits serverseitig gefiltert
  // (nur Registry-Zeilen mit parsebarem substitution_worm_key).
  const hsdRows = useMemo(
    () =>
      (hsdZeugen || []).map((z) => ({
        id: `hsd-${z.document_number}`,
        is_hsd: true,
        nachweis_type: "substitutionserklaerung",
        doc_type_label: "Substitutionserklärung",
        // Keine employee_id — der Gefahrstoff (Produktname) übernimmt
        // die Personen-Spalte (analog HSP: Einsatzbereich-Name).
        employee_name: z.product_name || "—",
        course_title: z.substitution_checked
          ? "Substitution geprüft — Ersatz dokumentiert"
          : z.substitution_reason_label || "Substitutionsprüfung",
        completed_at: z.owner_declared_at || z.created_at,
        document_number: z.document_number,
        status: undefined,
      })),
    [hsdZeugen]
  );

  // SDB-Integration: Zeugen kommen serverseitig gefiltert (nur Zeilen
  // mit vollständiger sdb_reference inkl. Original-Key).
  const sdbRows = useMemo(
    () =>
      (sdbZeugen || []).map((z) => ({
        id: `sdb-${z.document_number}`,
        is_sdb: true,
        nachweis_type: "sicherheitsdatenblatt",
        doc_type_label: "Sicherheitsdatenblatt",
        // Keine employee_id — der Gefahrstoff übernimmt die Personen-
        // Spalte (analog HSD).
        employee_name: z.product_name || "—",
        course_title: z.product_manufacturer
          ? `Hersteller-Original · ${z.product_manufacturer}`
          : "Hersteller-Original",
        completed_at: z.owner_declared_at || z.created_at,
        document_number: z.document_number,
        status: undefined,
      })),
    [sdbZeugen]
  );

  const displayDocs = useMemo(
    () => [...enriched, ...wkRows, ...hspRows, ...hsdRows, ...sdbRows],
    [enriched, wkRows, hspRows, hsdRows, sdbRows]
  );

  const employeeOptions = useMemo(() => {
    const map = new Map();
    for (const n of nachweise) {
      if (n.employee_id && !map.has(n.employee_id)) {
        map.set(n.employee_id, n.employee_name || "Unbekannt");
      }
    }
    for (const e of employees) {
      const full = `${e.first_name} ${e.last_name}`.trim();
      map.set(e.id, full);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [nachweise, employees]);

  const filteredNachweise = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return displayDocs
      .filter((n) => {
        if (filterEmployee !== ALL && n.employee_id !== filterEmployee) return false;
        if (filterDocType !== ALL && n.nachweis_type !== filterDocType) return false;
        if (filterStatus !== ALL && n.status !== filterStatus) return false;
        if (!inPeriod(n.completed_at, filterPeriod)) return false;
        if (q) {
          const haystack = [
            n.document_number,
            n.course_title,
            n.employee_name,
            n.employee_role,
            n.doc_type_label,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));
  }, [displayDocs, filterEmployee, filterDocType, filterStatus, filterPeriod, searchTerm]);

  const hasActiveFilter =
    filterEmployee !== ALL ||
    filterDocType !== ALL ||
    filterStatus !== ALL ||
    filterPeriod !== ALL ||
    searchTerm.trim() !== "";

  const resetFilters = () => {
    setFilterEmployee(ALL);
    setFilterPeriod(ALL);
    setFilterDocType(ALL);
    setFilterStatus(ALL);
    setSearchTerm("");
  };

  // --- Aufgaben-basierte Listen --------------------------------------------
  const offeneAufgaben = useMemo(
    () =>
      assignments
        .filter((a) => a.status === "offen")
        .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")),
    [assignments]
  );
  const ueberfaelligeAufgaben = useMemo(
    () =>
      assignments
        .filter((a) => a.status === "ueberfaellig")
        .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")),
    [assignments]
  );

  // --- Mitarbeiter-Liste (Name, offene Aufgaben, Nachweise, Status) --------
  const mitarbeiterListe = useMemo(() => {
    return employees
      .map((e) => {
        const offen = assignments.filter(
          (a) => a.employee_id === e.id && a.status === "offen"
        ).length;
        const ueberfaellig = assignments.filter(
          (a) => a.employee_id === e.id && a.status === "ueberfaellig"
        ).length;
        const nachweiseCount = nachweise.filter((n) => n.employee_id === e.id).length;
        const status = e.archived
          ? { label: "Archiviert", cls: "bg-slate-100 text-slate-600 border-slate-200" }
          : ueberfaellig > 0
          ? { label: "Überfällig", cls: "bg-rose-50 text-rose-800 border-rose-200" }
          : { label: "Aktiv", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" };
        return {
          id: e.id,
          name: `${e.first_name} ${e.last_name}`.trim(),
          role: e.role,
          offen,
          nachweiseCount,
          status,
          archived: !!e.archived,
        };
      })
      .sort((a, b) => {
        if (a.archived !== b.archived) return a.archived ? 1 : -1;
        return a.name.localeCompare(b.name, "de");
      });
  }, [employees, assignments, nachweise]);

  // --- Aktive Dokumentationswelten -----------------------------------------
  const aktiveWelten = useMemo(() => {
    return courses
      .filter((c) => c.is_world && c.released && !c.archived)
      .map((c) => ({
        id: c.id,
        title: c.title,
        version: c.version,
        zuweisungen: assignments.filter((a) => a.course_id === c.id).length,
      }))
      .sort((a, b) => (a.title || "").localeCompare(b.title || "", "de"));
  }, [courses, assignments]);

  // KPI-Werte (Backend liefert direkt, sonst lokaler Fallback).
  const kpi = {
    mitarbeiter: stats?.mitarbeiter_aktiv ?? employees.filter((e) => !e.archived).length,
    nachweise: stats?.nachweise ?? nachweise.length,
    offen: stats?.offen ?? offeneAufgaben.length,
    ueberfaellig: stats?.ueberfaellig ?? ueberfaelligeAufgaben.length,
    welten_aktiv: stats?.welten_aktiv ?? aktiveWelten.length,
  };

  const listCount = {
    mitarbeiter: mitarbeiterListe.length,
    nachweise: filteredNachweise.length,
    offen: offeneAufgaben.length,
    ueberfaellig: ueberfaelligeAufgaben.length,
    welten: aktiveWelten.length,
  }[activeView];

  // ---- Render: einzelne Listenansichten -----------------------------------

  const renderAssignmentTable = (items, testId) => {
    if (items.length === 0) {
      return (
        <EmptyList
          icon={activeView === "ueberfaellig" ? AlertTriangle : Clock}
          title={activeView === "ueberfaellig" ? "Keine überfälligen Aufgaben" : "Keine offenen Aufgaben"}
          text={
            activeView === "ueberfaellig"
              ? "Aktuell ist nichts überfällig – Ihr Betrieb ist insoweit prüfbereit."
              : "Aktuell sind keine Aufgaben offen."
          }
        />
      );
    }
    return (
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={testId}>
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left font-medium px-4 py-3">Mitarbeiter</th>
                <th className="text-left font-medium px-4 py-3">Dokumentationswelt</th>
                <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Fällig am</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((a) => {
                const badge = ASSIGNMENT_BADGE[a.status] || ASSIGNMENT_BADGE.offen;
                return (
                  <tr key={a.id} className="hover:bg-slate-50 transition-colors" data-testid={`aufgabe-row-${a.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {a.employee ? `${a.employee.first_name} ${a.employee.last_name}` : "–"}
                    </td>
                    <td className="px-4 py-3 text-slate-900 max-w-[320px] truncate" title={a.course?.title}>
                      {a.course?.title || "Unterweisung"}
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDate(a.due_date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={cn("inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium", badge.cls)}>
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  const renderMitarbeiter = () => {
    if (mitarbeiterListe.length === 0) {
      return <EmptyList icon={Users} title="Keine Mitarbeiter" text="Legen Sie Mitarbeiter an, um Unterweisungen zuzuweisen." />;
    }
    return (
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pruefmappe-list-mitarbeiter">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left font-medium px-4 py-3">Mitarbeiter</th>
                <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Offene Aufgaben</th>
                <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Nachweise</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {mitarbeiterListe.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50 transition-colors" data-testid={`mitarbeiter-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {m.name}
                    {m.role && <div className="text-xs text-slate-500 font-normal mt-0.5">{m.role}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{m.offen}</td>
                  <td className="px-4 py-3 text-slate-700">{m.nachweiseCount}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium", m.status.cls)}>
                      {m.status.label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  const renderWelten = () => {
    if (aktiveWelten.length === 0) {
      return <EmptyList icon={BookOpen} title="Keine aktiven Welten" text="Sobald eine Dokumentationswelt freigegeben ist, erscheint sie hier." />;
    }
    return (
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pruefmappe-list-welten">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left font-medium px-4 py-3">Dokumentationswelt</th>
                <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Version</th>
                <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Zuweisungen</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {aktiveWelten.map((w) => (
                <tr key={w.id} className="hover:bg-slate-50 transition-colors" data-testid={`welt-row-${w.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 max-w-[360px] truncate" title={w.title}>{w.title}</td>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">v{w.version || "1.0"}</td>
                  <td className="px-4 py-3 text-slate-700">{w.zuweisungen}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium bg-emerald-50 text-emerald-800 border-emerald-200">
                      Aktiv
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  const renderNachweise = () => {
    if (displayDocs.length === 0) {
      return (
        <div data-testid="pruefmappe-empty">
          <EmptyList
            icon={FileCheck2}
            title="Noch keine Nachweise"
            text="Sobald ein Mitarbeiter eine Unterweisung erledigt oder eine Welt freigegeben wird, erscheint hier automatisch ein Nachweis."
          />
        </div>
      );
    }
    return (
      <>
        {/* Filter-Leiste (nur für die Nachweis-Liste relevant) */}
        <Card className="bg-white border border-slate-200 shadow-sm p-4 print:hidden" data-testid="pruefmappe-filters">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 shrink-0 text-slate-500">
              <FilterIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Filter</span>
            </div>
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger className="w-[200px] h-9" data-testid="pruefmappe-filter-employee">
                <SelectValue placeholder="Alle Mitarbeiter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Alle Mitarbeiter</SelectItem>
                {employeeOptions.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPeriod} onValueChange={setFilterPeriod}>
              <SelectTrigger className="w-[170px] h-9" data-testid="pruefmappe-filter-period">
                <SelectValue placeholder="Zeitraum" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterDocType} onValueChange={setFilterDocType}>
              <SelectTrigger className="w-[170px] h-9" data-testid="pruefmappe-filter-doctype">
                <SelectValue placeholder="Dokumenttyp" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Alle Dokumenttypen</SelectItem>
                <SelectItem value="unterweisung">Unterweisung</SelectItem>
                <SelectItem value="weltfreigabe">Weltfreigabe</SelectItem>
                <SelectItem value="wirksamkeitskontrolle">Wirksamkeitskontrollen</SelectItem>
                <SelectItem value="handschutzplan">Handschutzplan</SelectItem>
                <SelectItem value="substitutionserklaerung">Substitutionserklärung</SelectItem>
                <SelectItem value="sicherheitsdatenblatt">Sicherheitsdatenblatt</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px] h-9" data-testid="pruefmappe-filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Alle Status</SelectItem>
                <SelectItem value="aktuell">Aktuell</SelectItem>
                <SelectItem value="bald_faellig">Bald fällig</SelectItem>
                <SelectItem value="abgelaufen">Abgelaufen</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Suche nach Nummer, Titel, Name…"
                data-testid="pruefmappe-search"
                className="h-9 pl-9"
              />
            </div>
            {hasActiveFilter && (
              <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="pruefmappe-reset-filters" className="text-slate-500 h-9">
                <X className="w-3 h-3 mr-1" />
                Zurücksetzen
              </Button>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500" data-testid="pruefmappe-filter-count">
            {filteredNachweise.length === displayDocs.length
              ? `${displayDocs.length} Dokumente`
              : `${filteredNachweise.length} von ${displayDocs.length} Dokumenten sichtbar`}
          </div>
        </Card>

        {filteredNachweise.length === 0 ? (
          <Card className="bg-white border border-slate-200 shadow-sm p-10 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
              <Search className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-semibold text-slate-900">Keine Treffer</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Mit den aktuellen Filtern wurde nichts gefunden.</p>
            <Button variant="outline" size="sm" onClick={resetFilters} data-testid="pruefmappe-empty-reset" className="mt-4">
              Filter zurücksetzen
            </Button>
          </Card>
        ) : (
          <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="pruefmappe-table-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pruefmappe-table">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
                  <tr>
                    <th className="text-left font-medium px-4 py-3">Mitarbeiter</th>
                    <th className="text-left font-medium px-4 py-3">Dokumentationswelt</th>
                    <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Dokumenttyp</th>
                    <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Erledigt am</th>
                    <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Version</th>
                    <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Nachweis-Nr.</th>
                    <th className="text-left font-medium px-4 py-3">Status</th>
                    <th className="text-right font-medium px-4 py-3 print:hidden">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredNachweise.map((n) =>
                    n.is_wk ? (
                      <tr key={n.id} className="hover:bg-slate-50 transition-colors" data-testid={`wk-row-${n.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900" data-testid={`wk-checkedby-${n.id}`}>
                          {n.checked_by_name || "—"}
                          <div className="text-xs text-slate-500 font-normal mt-0.5">Bestätigt durch</div>
                        </td>
                        <td className="px-4 py-3 text-slate-900 max-w-[280px] truncate" title={n.course_title}>
                          {n.course_title}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden md:table-cell whitespace-nowrap" data-testid={`wk-doctype-${n.id}`}>
                          {n.doc_type_label}
                        </td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`wk-date-${n.id}`}>
                          {formatDate(n.completed_at)}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden lg:table-cell whitespace-nowrap">
                          v{n.course_version || "1.0"}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap" data-testid={`wk-number-${n.id}`}>
                          {n.document_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="space-y-1">
                            <span
                              className={cn(
                                "inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium",
                                WK_RESULT_BADGE[n.result] || "bg-slate-50 text-slate-700 border-slate-200"
                              )}
                              data-testid={`wk-result-${n.id}`}
                            >
                              {WK_RESULT_LABEL[n.result] || "—"}
                            </span>
                            <div
                              className={cn("text-[11px]", n.pdf_available ? "text-emerald-600" : "text-rose-600")}
                              data-testid={`wk-worm-${n.id}`}
                            >
                              {n.pdf_available ? "WORM archiviert" : "WORM nicht verfügbar"}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right print:hidden">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!n.pdf_available}
                            onClick={() => handleOpenWkPdf(n.id)}
                            data-testid={`open-wk-pdf-${n.id}`}
                            title={n.pdf_available ? "" : "PDF nicht verfügbar"}
                            className="h-8 text-slate-600 hover:text-slate-900"
                          >
                            <Eye className="w-4 h-4 mr-1.5" />
                            {n.pdf_available ? "PDF öffnen" : "PDF nicht verfügbar"}
                          </Button>
                        </td>
                      </tr>
                    ) : n.is_hsp ? (
                      <tr key={n.id} className="hover:bg-slate-50 transition-colors" data-testid={`hsp-row-${n.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900" data-testid={`hsp-bereich-${n.id}`}>
                          {n.employee_name || "—"}
                          <div className="text-xs text-slate-500 font-normal mt-0.5">Einsatzbereich</div>
                        </td>
                        <td className="px-4 py-3 text-slate-900 max-w-[280px] truncate" title={n.course_title}>
                          {n.course_title}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden md:table-cell whitespace-nowrap" data-testid={`hsp-doctype-${n.id}`}>
                          {n.doc_type_label}
                        </td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`hsp-date-${n.id}`}>
                          {formatDate(n.completed_at)}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden lg:table-cell whitespace-nowrap">
                          —
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap" data-testid={`hsp-number-${n.id}`}>
                          {n.document_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-[11px] text-emerald-600" data-testid={`hsp-worm-${n.id}`}>
                            WORM archiviert
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right print:hidden">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenHspZeuge(n.document_number)}
                            data-testid={`open-hsp-zeuge-${n.id}`}
                            className="h-8 text-slate-600 hover:text-slate-900"
                          >
                            <Eye className="w-4 h-4 mr-1.5" />
                            Beleg öffnen
                          </Button>
                        </td>
                      </tr>
                    ) : n.is_hsd ? (
                      <tr key={n.id} className="hover:bg-slate-50 transition-colors" data-testid={`hsd-row-${n.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900" data-testid={`hsd-produkt-${n.id}`}>
                          {n.employee_name || "—"}
                          <div className="text-xs text-slate-500 font-normal mt-0.5">Gefahrstoff</div>
                        </td>
                        <td className="px-4 py-3 text-slate-900 max-w-[280px] truncate" title={n.course_title}>
                          {n.course_title}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden md:table-cell whitespace-nowrap" data-testid={`hsd-doctype-${n.id}`}>
                          {n.doc_type_label}
                        </td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`hsd-date-${n.id}`}>
                          {formatDate(n.completed_at)}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden lg:table-cell whitespace-nowrap">
                          —
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap" data-testid={`hsd-number-${n.id}`}>
                          {n.document_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-[11px] text-emerald-600" data-testid={`hsd-worm-${n.id}`}>
                            WORM archiviert
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right print:hidden">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenHsdZeuge(n.document_number)}
                            data-testid={`open-hsd-zeuge-${n.id}`}
                            className="h-8 text-slate-600 hover:text-slate-900"
                          >
                            <Eye className="w-4 h-4 mr-1.5" />
                            Beleg öffnen
                          </Button>
                        </td>
                      </tr>
                    ) : n.is_sdb ? (
                      <tr key={n.id} className="hover:bg-slate-50 transition-colors" data-testid={`sdb-row-${n.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900" data-testid={`sdb-produkt-${n.id}`}>
                          {n.employee_name || "—"}
                          <div className="text-xs text-slate-500 font-normal mt-0.5">Gefahrstoff</div>
                        </td>
                        <td className="px-4 py-3 text-slate-900 max-w-[280px] truncate" title={n.course_title}>
                          {n.course_title}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden md:table-cell whitespace-nowrap" data-testid={`sdb-doctype-${n.id}`}>
                          {n.doc_type_label}
                        </td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`sdb-date-${n.id}`}>
                          {formatDate(n.completed_at)}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden lg:table-cell whitespace-nowrap">
                          —
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap" data-testid={`sdb-number-${n.id}`}>
                          {n.document_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-[11px] text-emerald-600" data-testid={`sdb-worm-${n.id}`}>
                            WORM archiviert
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right print:hidden">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenSdbOriginal(n.document_number)}
                            data-testid={`open-sdb-original-${n.id}`}
                            className="h-8 text-slate-600 hover:text-slate-900"
                          >
                            <Eye className="w-4 h-4 mr-1.5" />
                            Beleg öffnen
                          </Button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={n.id} className="hover:bg-slate-50 transition-colors" data-testid={`nachweis-row-${n.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900" data-testid={`nachweis-employee-${n.id}`}>
                          {n.employee_name || <span className="text-slate-400">{n.released_by || "Unternehmer"}</span>}
                          {n.employee_role && <div className="text-xs text-slate-500 font-normal mt-0.5">{n.employee_role}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-900 max-w-[280px] truncate" data-testid={`nachweis-welt-${n.id}`} title={n.course_title}>
                          {n.course_title}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden md:table-cell whitespace-nowrap" data-testid={`nachweis-doctype-${n.id}`}>
                          {n.doc_type_label}
                        </td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`nachweis-date-${n.id}`}>
                          {formatDate(n.completed_at)}
                        </td>
                        <td className="px-4 py-3 text-slate-700 hidden lg:table-cell whitespace-nowrap" data-testid={`nachweis-version-${n.id}`}>
                          v{n.course_version || "1.0"}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap" data-testid={`nachweis-number-${n.id}`}>
                          {n.document_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={cn("inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium", STATUS_BADGE[n.status])} data-testid={`nachweis-status-${n.id}`}>
                            {STATUS_LABEL[n.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right print:hidden">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/unternehmer/nachweis/${n.id}`)}
                            data-testid={`open-nachweis-${n.id}`}
                            className="h-8 text-slate-600 hover:text-slate-900"
                          >
                            <Eye className="w-4 h-4 mr-1.5" />
                            Öffnen
                          </Button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </>
    );
  };

  return (
    <div className="space-y-6" data-testid="pruefmappe-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">Archiv</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">BG Prüfmappe</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Ist mein Betrieb prüfbereit? Tippen Sie auf eine Kennzahl, um die passende
            Liste zu sehen – Mitarbeiter, Nachweise, offene und überfällige Aufgaben oder
            aktive Dokumentationswelten.
          </p>
        </div>
        <Button
          onClick={handleDownloadPackage}
          disabled={downloadingPackage}
          className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
          data-testid="pruefmappe-download-bg-package"
        >
          {downloadingPackage ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <PackageCheck className="w-4 h-4 mr-2" />
          )}
          BG-Prüfpaket herunterladen
        </Button>
      </div>

      {/* Firmen-Header für BG-Prüfer (unverändert) */}
      {company && (
        <Card className="bg-white border border-slate-200 shadow-sm p-5" data-testid="pruefmappe-company-header">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Betrieb</div>
              <div className="font-heading text-lg font-semibold text-slate-900">{company.name}</div>
              {company.address && <div className="text-sm text-slate-700 mt-0.5">{company.address}</div>}
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1.5 text-sm text-slate-600">
                {company.contact_person && (
                  <div><span className="text-slate-400">Verantwortliche Person: </span>{company.contact_person}</div>
                )}
                {company.bg_name && (
                  <div>
                    <span className="text-slate-400">BG: </span>
                    {company.bg_name}{company.bg_id ? ` · Nr. ${company.bg_id}` : ""}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right shrink-0 hidden sm:block">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Stand</div>
              <div className="font-heading text-sm font-semibold text-slate-900 mt-1">
                {new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Kennzahlenleiste = Filter */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pruefmappe-kpis">
        <KpiCard label="Mitarbeiter" value={kpi.mitarbeiter} icon={Users} testId="kpi-mitarbeiter" active={activeView === "mitarbeiter"} onClick={() => handleKpiClick("mitarbeiter")} />
        <KpiCard label="Nachweise gesamt" value={kpi.nachweise} icon={FileCheck2} iconSrc="/kachel-icons/nachweise_gesamt.png" tone="success" testId="kpi-nachweise" active={activeView === "nachweise"} onClick={() => handleKpiClick("nachweise")} />
        <KpiCard label="Offene Aufgaben" value={kpi.offen} icon={Clock} iconSrc="/kachel-icons/offene_aufgaben.png" tone={kpi.offen > 0 ? "warning" : "default"} testId="kpi-offen" active={activeView === "offen"} onClick={() => handleKpiClick("offen")} />
        <KpiCard label="Überfällig" value={kpi.ueberfaellig} icon={AlertTriangle} iconSrc="/ampel/ampel-rot.png" tone={kpi.ueberfaellig > 0 ? "danger" : "default"} testId="kpi-ueberfaellig" active={activeView === "ueberfaellig"} onClick={() => handleKpiClick("ueberfaellig")} />
        <KpiCard label="Dokumentationen aktiv" value={kpi.welten_aktiv} icon={BookOpen} iconSrc="/kachel-icons/dokumentationen_aktiv.png" testId="kpi-welten" active={activeView === "welten"} onClick={() => handleKpiClick("welten")} />
      </div>

      {/* Listenbereich – dynamisch je aktiver Kennzahl */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-lg font-semibold text-slate-900" data-testid="pruefmappe-list-title">
          {VIEW_LABEL[activeView]}{" "}
          <span className="text-slate-400 font-normal">({listCount})</span>
        </h2>
      </div>

      {activeView === "nachweise" && renderNachweise()}
      {activeView === "offen" && renderAssignmentTable(offeneAufgaben, "pruefmappe-list-offen")}
      {activeView === "ueberfaellig" && renderAssignmentTable(ueberfaelligeAufgaben, "pruefmappe-list-ueberfaellig")}
      {activeView === "mitarbeiter" && renderMitarbeiter()}
      {activeView === "welten" && renderWelten()}
    </div>
  );
}
